
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { api } from "../api/apiClient";

import {
  ThaiPaperBackground,
  Section,
  OrnamentalDivider,
} from "../components/layout/Sections";

import SiteHeader from "../components/SiteHeader";
import ScrollToTopButton from "../components/ScrollToTopButton";

// ============================================================
// CONFIGURATION
// ============================================================

const SITE_URL = "https://the-mea-thai-cuisine.com";

const BUSINESS_NAME = "The Mea Thai Cuisine";
const APP_NAME = "The Mea Thai Cuisine";

const LAST_UPDATED = "October 16, 2026";

const DEFAULT_ORDER_LINK =
  "https://polite-mud-02f9f1a0f.6.azurestaticapps.net";

// ============================================================
// DELETION INFORMATION
// ============================================================

const deletionSections = [
  {
    id: "delete-in-app",
    title: "1. Delete Your Account in the Mobile App",
    paragraphs: [
      "If you have access to The Mea Thai Cuisine mobile application, you can permanently delete your account directly from the app.",
      "Follow these steps:",
    ],
    steps: [
      "Open The Mea Thai Cuisine mobile application.",
      "Sign in using your registered account.",
      "Navigate to your Account or Profile settings.",
      "Select Delete Account.",
      "Review the information and confirm your deletion request.",
    ],
    closing:
      "Once the deletion is completed, your account will no longer be available for login. This action cannot be undone.",
  },
  {
    id: "request-deletion",
    title: "2. Request Account Deletion Without the App",
    paragraphs: [
      "You can request account deletion even if you no longer have the mobile app installed, cannot sign in, or no longer have access to your device.",
      "Contact The Mea Thai Cuisine through our restaurant website and tell us that you would like to permanently delete your mobile application account.",
      "Please include the email address associated with your account so we can identify the correct account.",
      "For your protection, we may ask you to verify account ownership before processing the request. Never send us your password or full payment card number.",
    ],
    steps: [
      "Open the Contact Our Restaurant link below.",
      "Provide the email address associated with your account.",
      "State that you are requesting permanent deletion of your Mea Thai Cuisine mobile app account.",
      "Complete any necessary account ownership verification.",
    ],
    closing:
      "You do not need to reinstall the mobile application to submit a deletion request.",
  },
  {
    id: "deleted-data",
    title: "3. Information Deleted",
    paragraphs: [
      "When your account is deleted, we remove your mobile application login account and its associated authentication information.",
      "Account-related information subject to deletion includes:",
    ],
    bullets: [
      "Mobile application login account and authentication credentials.",
      "Stored password hashes.",
      "Access credentials and stored refresh token information.",
      "Password reset codes and related authentication records.",
      "Account settings and saved preferences that are no longer required.",
      "Personal information associated with the app account that does not need to be retained for legitimate business or legal purposes.",
    ],
    closing:
      "Some records may exist separately within our restaurant ordering or point-of-sale systems. Eligible personal information in those systems will be deleted or anonymized as part of the applicable deletion process.",
  },
  {
    id: "retained-data",
    title: "4. Information That May Be Retained",
    paragraphs: [
      "Deleting your mobile application account does not necessarily require the immediate deletion of every historical restaurant transaction.",
      "Certain records may need to be retained for legitimate business and legal purposes, including:",
    ],
    bullets: [
      "Completed order and purchase transaction records.",
      "Payment, refund, and accounting information.",
      "Records required for tax reporting or legal compliance.",
      "Information reasonably necessary to investigate fraud, resolve disputes, or protect our business and customers.",
    ],
    closing:
      "Where we must retain information, we limit its use to the applicable purpose and retain it only for the period reasonably necessary or legally required. After that period, eligible information will be securely deleted or anonymized.",
  },
  {
    id: "processing",
    title: "5. Processing Your Request",
    paragraphs: [
      "Account deletion performed through the mobile application takes effect when the deletion operation is successfully completed.",
      "Requests submitted through our website are reviewed by our team. We may need additional information to verify that the request is being made by the account owner.",
      "We aim to process verified requests promptly, subject to applicable legal requirements and any necessary verification.",
      "If certain information must be retained, we may explain the categories of information retained and the reasons for retention.",
    ],
  },
  {
    id: "important-information",
    title: "6. Important Information",
    paragraphs: [
      "Account deletion is permanent. After your account has been deleted, you will no longer be able to sign in using that account.",
      "Deleting the mobile application from your phone does not automatically delete your account or information stored on our servers.",
      "If you choose to use our mobile application again after deletion, you may register for a new account.",
      "Deleting your mobile application account does not automatically cancel, refund, or alter a food order already placed with our restaurant. Please contact us separately for assistance with an existing order.",
    ],
  },
];

// ============================================================
// REUSABLE SECTION
// ============================================================

function DeletionSection({ section }) {
  return (
    <article
      id={section.id}
      className="scroll-mt-24 rounded-3xl bg-white/85 backdrop-blur-sm shadow-sm border border-gray-100 p-6 md:p-9"
    >
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-5">
        {section.title}
      </h2>

      <div className="space-y-4 text-gray-700 leading-8">
        {section.paragraphs?.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}

        {section.steps && (
          <ol className="list-decimal pl-6 space-y-3">
            {section.steps.map((step, index) => (
              <li key={index} className="pl-1">
                {step}
              </li>
            ))}
          </ol>
        )}

        {section.bullets && (
          <ul className="list-disc pl-6 space-y-2">
            {section.bullets.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        )}

        {section.closing && (
          <p>{section.closing}</p>
        )}
      </div>

      {section.id === "request-deletion" && (
        <div className="mt-7 rounded-2xl bg-red-50 border border-red-100 p-5 md:p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            Request Account Deletion
          </h3>

          <p className="text-gray-700 leading-7 mb-5">
            You can submit your request through our
            restaurant's contact page. Please include your
            account email address and specify that you
            would like your mobile application account
            permanently deleted.
          </p>

          <Link
            to="/"
            state={{ scrollTo: "contact" }}
            className="inline-flex items-center justify-center rounded-full bg-red-600 text-white font-bold px-7 py-3 shadow-sm hover:bg-red-700 transition"
          >
            Contact Our Restaurant
          </Link>
        </div>
      )}
    </article>
  );
}

// ============================================================
// PAGE
// ============================================================

function DeleteAccountPage() {
  const [orderLink, setOrderLink] = useState(
    DEFAULT_ORDER_LINK
  );

  const [isOrderingEnabled, setIsOrderingEnabled] =
    useState(true);

  const [mobileNavOpen, setMobileNavOpen] =
    useState(false);

  const [showScrollTop, setShowScrollTop] =
    useState(false);

  // ==========================================================
  // LOAD SITE SETTINGS
  // ==========================================================

  useEffect(() => {
    let active = true;

    const loadSiteSettings = async () => {
      const [flagResult, linkResult] =
        await Promise.allSettled([
          api.get(
            "/TCVariable/value/Mea-Online-Ordering-Website-On-Off",
            {
              params: {
                name: "Mea-Online-Ordering-Website-On-Off",
              },
            }
          ),
          api.get("/TCVariable/link"),
        ]);

      if (!active) return;

      if (flagResult.status === "fulfilled") {
        const rawValue = String(
          flagResult.value.data ?? ""
        )
          .trim()
          .toLowerCase();

        setIsOrderingEnabled(
          !["off", "0", "false"].includes(rawValue)
        );
      }

      if (
        linkResult.status === "fulfilled" &&
        typeof linkResult.value.data === "string" &&
        linkResult.value.data.startsWith("https://")
      ) {
        setOrderLink(linkResult.value.data);
      }
    };

    loadSiteSettings();

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    handleScroll();

    window.addEventListener(
      "scroll",
      handleScroll
    );

    return () => {
      active = false;

      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <ThaiPaperBackground>
      <Helmet>
        <title>
          Delete Account | The Mea Thai Cuisine
        </title>

        <meta
          name="description"
          content="Learn how to permanently delete your The Mea Thai Cuisine mobile app account and request removal of your associated personal information."
        />

        <link
          rel="canonical"
          href={`${SITE_URL}/delete-account`}
        />

        <meta
          property="og:title"
          content="Delete Account | The Mea Thai Cuisine"
        />

        <meta
          property="og:description"
          content="Request deletion of your Mea Thai Cuisine mobile application account and learn how your associated data is handled."
        />

        <meta
          property="og:url"
          content={`${SITE_URL}/delete-account`}
        />
      </Helmet>

      <SiteHeader
        orderLink={orderLink}
        isOrderingEnabled={isOrderingEnabled}
        mobileNavOpen={mobileNavOpen}
        setMobileNavOpen={setMobileNavOpen}
      />

      {/* ==================================================== */}
      {/* HERO                                                 */}
      {/* ==================================================== */}

      <Section tone="warm">
        <div className="max-w-5xl mx-auto py-10 md:py-16 text-center">
          <div className="inline-flex items-center rounded-full bg-red-50 border border-red-100 px-4 py-2 text-sm font-semibold text-red-700 mb-5">
            Account &amp; Data Privacy
          </div>

          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-gray-900 mb-5">
            Delete Your Account
          </h1>

          <p className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed text-gray-700">
            We respect your privacy and your right to
            control your personal information. Learn
            how to permanently delete your{" "}
            <strong>{APP_NAME}</strong> mobile app
            account and associated data.
          </p>

          <p className="text-sm text-gray-500 mt-5">
            Last Updated: {LAST_UPDATED}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a
              href="#request-deletion"
              className="rounded-full bg-red-600 text-white font-bold px-7 py-3 shadow-sm hover:bg-red-700 transition"
            >
              Request Account Deletion
            </a>

            <Link
              to="/"
              className="rounded-full bg-white border border-gray-300 text-gray-800 font-semibold px-7 py-3 hover:bg-gray-50 transition"
            >
              Back to Restaurant
            </Link>
          </div>
        </div>
      </Section>

      {/* ==================================================== */}
      {/* OVERVIEW                                             */}
      {/* ==================================================== */}

      <Section tone="light">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl bg-white/85 backdrop-blur-sm shadow-sm border border-gray-100 p-7 md:p-10">
            <p className="text-sm uppercase tracking-[0.18em] font-semibold text-red-700 mb-3">
              Account Deletion Information
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
              Your Account. Your Choice.
            </h2>

            <div className="space-y-4 text-gray-700 leading-8">
              <p>
                <strong>{BUSINESS_NAME}</strong>,
                located in Plymouth, Massachusetts,
                provides online ordering services
                through its website and customer-facing
                mobile application.
              </p>

              <p>
                If you have created an account using
                our Android mobile application, you
                may request permanent deletion of
                your account and associated personal
                information.
              </p>

              <p>
                You can initiate this process from
                within the mobile application or
                request assistance through our
                restaurant website.
              </p>

              <p>
                Certain historical business records,
                including completed transactions,
                may be retained when necessary for
                legitimate business or legal purposes.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <OrnamentalDivider />

      {/* ==================================================== */}
      {/* DELETION SECTIONS                                    */}
      {/* ==================================================== */}

      <Section tone="warm">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[240px_minmax(0,1fr)] gap-7 items-start">
          {/* TABLE OF CONTENTS */}

          <nav
            aria-label="Account deletion sections"
            className="hidden lg:block sticky top-24 rounded-2xl bg-white/85 border border-gray-100 shadow-sm p-5"
          >
            <h2 className="font-bold text-gray-900 mb-4">
              On This Page
            </h2>

            <div className="space-y-3">
              {deletionSections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block text-sm text-gray-600 hover:text-red-700 transition"
                >
                  {section.title}
                </a>
              ))}

              <a
                href="#contact-deletion"
                className="block text-sm font-semibold text-red-700 hover:underline"
              >
                7. Contact Us
              </a>
            </div>
          </nav>

          {/* MAIN CONTENT */}

          <div className="space-y-5 min-w-0">
            {deletionSections.map((section) => (
              <DeletionSection
                key={section.id}
                section={section}
              />
            ))}

            {/* ============================================== */}
            {/* CONTACT SECTION                                */}
            {/* ============================================== */}

            <article
              id="contact-deletion"
              className="scroll-mt-24 rounded-3xl bg-white/90 backdrop-blur-sm shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="bg-red-700 text-white p-6 md:p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-red-100 mb-2">
                  We're Here to Help
                </p>

                <h2 className="text-2xl md:text-3xl font-bold">
                  7. Contact Us
                </h2>
              </div>

              <div className="p-6 md:p-9 space-y-5 text-gray-700 leading-8">
                <p>
                  If you need assistance deleting
                  your account, cannot access the
                  mobile application, or have
                  questions about how your
                  information is handled, please
                  contact our restaurant.
                </p>

                <div className="rounded-2xl bg-gray-50 border border-gray-100 p-5">
                  <p className="font-bold text-gray-900 text-lg">
                    {BUSINESS_NAME}
                  </p>

                  <p>
                    Plymouth, Massachusetts, USA
                  </p>

                  <p>
                    Website:{" "}
                    <a
                      href={SITE_URL}
                      className="text-red-700 underline break-words"
                    >
                      {SITE_URL}
                    </a>
                  </p>

                  <p>
                    Mobile Application: {APP_NAME}
                  </p>
                </div>

                <div className="rounded-2xl bg-red-50 border border-red-100 p-5">
                  <h3 className="font-bold text-gray-900 mb-3">
                    What to Include in Your Request
                  </h3>

                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      Your account email address.
                    </li>

                    <li>
                      Your name, if needed to help
                      locate the account.
                    </li>

                    <li>
                      A statement requesting
                      permanent account deletion.
                    </li>
                  </ul>
                </div>

                <p>
                  For security reasons, do not
                  include your password, payment
                  card information, or other
                  sensitive credentials in your
                  request.
                </p>

                <Link
                  to="/"
                  state={{ scrollTo: "contact" }}
                  className="inline-flex items-center justify-center rounded-full bg-red-600 text-white font-bold px-7 py-3 shadow-sm hover:bg-red-700 transition"
                >
                  Contact Our Restaurant
                </Link>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    For additional details about
                    how we collect, use, and retain
                    information, please review our{" "}
                    <Link
                      to="/privacy-policy"
                      className="text-red-700 font-semibold underline"
                    >
                      Privacy Policy
                    </Link>.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </Section>

      {/* ==================================================== */}
      {/* FOOTER CTA                                           */}
      {/* ==================================================== */}

      <Section tone="light">
        <div className="max-w-4xl mx-auto rounded-3xl bg-white/80 backdrop-blur-sm shadow-sm border border-gray-100 p-7 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            We're Here to Help
          </h2>

          <p className="text-gray-700 max-w-2xl mx-auto mb-6">
            Thank you for being part of The Mea
            Thai Cuisine family. We appreciate
            your trust and are committed to
            respecting your privacy.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex rounded-full bg-red-600 text-white font-bold px-7 py-3 hover:bg-red-700 transition"
            >
              Return to Home Page
            </Link>

            <Link
              to="/privacy-policy"
              className="inline-flex rounded-full bg-white border border-gray-300 text-gray-800 font-semibold px-7 py-3 hover:bg-gray-50 transition"
            >
              View Privacy Policy
            </Link>
          </div>
        </div>
      </Section>

      <ScrollToTopButton show={showScrollTop} />
    </ThaiPaperBackground>
  );
}

export default DeleteAccountPage;
